import {
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { rawApi } from '../../api/client';
import { ApiError } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { enumOptions } from '../../lib/enums';
import { notifyDone, notifyError } from '../../lib/notify';
import type { Spot } from './FloorMap';
import {
  DECOR_DEFAULTS,
  KIND_DEFAULTS,
  newSpotOfKind,
  seatingTraits,
  shapesFor,
  traitsPatch,
} from './shapes';

const FLOOR_PATH = '/admin/v1/floor/zones';

/** What a new place starts as: a four-top, since that is most of what a bar has. */
const NEW_SPOT = {
  label: '',
  kind: 'table',
  seats: 4,
  mobility: 'fixed',
  combinable: false,
  shape: 'rect',
  width: 64,
  height: 64,
  active: true,
  assignable: true,
};

export interface SpotDraftPosition {
  zoneId: string;
  /** What the designer picked off the palette before clicking the floor. */
  kind?: string;
  x: number;
  y: number;
}

/**
 * The property editor for one place on the floor, opened where it stands.
 *
 * Replaces a pair of CRUD tables that listed the same seating underneath the map. A table of
 * furniture is a strange thing to read: you already know what your bar looks like, and the
 * question you actually have is about *this* table — the one you can see and point at.
 */
export function SpotPropertiesModal({
  spot,
  createAt,
  onClose,
}: {
  /** Editing an existing place. */
  spot?: Spot | null;
  /** Adding a new one at a point on the plan. */
  createAt?: SpotDraftPosition | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const opened = Boolean(spot || createAt);
  const [draft, setDraft] = useState<Record<string, unknown>>(NEW_SPOT);

  useEffect(() => {
    if (spot) {
      setDraft({
        label: spot.label,
        kind: spot.kind,
        seats: spot.seats,
        mobility: spot.mobility,
        combinable: spot.combinable,
        shape: spot.shape,
        width: spot.width,
        height: spot.height,
        active: spot.active,
        assignable: spot.assignable !== false,
      });
    } else if (createAt) {
      // The palette already said what this is; arriving as a table and making the designer change
      // it is how the old flow made scenery feel like something only the database could place.
      setDraft(createAt.kind ? newSpotOfKind(createAt.kind) : { ...NEW_SPOT });
    }
  }, [spot, createAt]);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: listKey(FLOOR_PATH) });

  /**
   * The minimum is applied when the box is left, never while it is being typed in.
   *
   * `Math.max(8, …)` on every keystroke meant the "1" of "120" became "8" before the "2" was
   * reached, so any size starting with a digit below the minimum was literally untypeable. Mantine
   * clamps on blur for the same reason; this is the belt to that braces, because a value can also
   * leave here by pressing Save without the box ever losing focus.
   */
  const sized = (v: unknown, min: number) => Math.max(min, Number(v) || min);

  const save = useMutation({
    mutationFn: () => {
      // Half-typed sizes are settled here, not while he was typing them.
      const body = { ...draft, width: sized(draft.width, 8), height: sized(draft.height, 8) };
      return spot
        ? rawApi('PATCH', `/admin/v1/floor/spots/${spot.id}`, body)
        : rawApi('POST', '/admin/v1/floor/spots', {
            ...body,
            zoneId: createAt?.zoneId,
            x: createAt?.x ?? 0,
            y: createAt?.y ?? 0,
          });
    },
    onSuccess: () => {
      refresh();
      notifyDone(spot ? 'Saved' : 'Added', `${String(draft.label || 'It')} is on the plan.`);
      onClose();
    },
    onError: (err) => notifyError(err, spot ? 'save this place' : 'add this place'),
  });

  /**
   * Removing it for good.
   *
   * "Take off the plan" is the right answer for a table an order has been seated at — deleting that
   * takes the history with it. It is the WRONG answer for a palm tree: nothing anywhere references
   * one, so a mis-dropped tree used to be permanent, drawn on the plan and grayed out forever. With
   * a palette that places on a single click, mis-drops are the common case, not the rare one.
   *
   * The server decides, not this screen: it refuses with `spot_has_orders` when history would be
   * lost, and that refusal is offered as taking it off the plan instead — so the safe route is one
   * click away exactly when it is the one that applies.
   */
  const remove = useMutation({
    mutationFn: () => rawApi('DELETE', `/admin/v1/floor/spots/${spot?.id}`),
    onSuccess: () => {
      refresh();
      notifyDone('Removed', `${String(spot?.label || 'It')} is gone from the plan.`);
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'spot_has_orders') {
        modals.openConfirmModal({
          title: 'Guests have been seated here',
          children: (
            <Text size="sm">
              Deleting it would take those orders' history with it. It can be taken off the plan instead — it
              stops being somewhere anyone can be seated, and the past stays intact.
            </Text>
          ),
          labels: { confirm: 'Take it off the plan', cancel: 'Leave it alone' },
          onConfirm: () => setInUse.mutate(false),
        });
        return;
      }
      notifyError(err, 'remove this place');
    },
  });

  // Removing is deactivating, not deleting: tabs and orders reference a place by id, and erasing it
  // would take their history with it. Off the plan, still in the past.
  //
  // It goes both ways because taking a table off was one-way: a mis-click on "Take off the plan"
  // left the table drawn but dead, with nothing anywhere to bring it back — the shape stayed on
  // the map looking like furniture, and staff could not seat anyone at it.
  const setInUse = useMutation({
    mutationFn: (inUse: boolean) => rawApi('PATCH', `/admin/v1/floor/spots/${spot?.id}`, { active: inUse }),
    onSuccess: (_result, inUse) => {
      refresh();
      notifyDone(
        inUse ? 'Back on the plan' : 'Taken off the plan',
        inUse
          ? `${spot?.label} can be seated again.`
          : `${spot?.label} is no longer in use. Past orders keep it.`,
      );
      onClose();
    },
    onError: (err, inUse) => notifyError(err, inUse ? 'put this back on the plan' : 'take this off the plan'),
  });

  const set = (patch: Record<string, unknown>) => setDraft((d) => ({ ...d, ...patch }));
  const busy = save.isPending || setInUse.isPending;
  const seats = Number(draft.seats) || 0;
  const assignable = draft.assignable !== false;
  // What this kind of thing can actually be asked. A lounger seats one because that is what a
  // lounger is, and joining is something tables do — so neither question is put to it.
  const traits = seatingTraits(String(draft.kind), assignable);
  const offThePlan = Boolean(spot) && spot?.active === false;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={spot ? spot.label || 'This place' : 'Add to the plan'}
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {offThePlan && (
          <Text size="sm" c="dimmed">
            This is off the plan — nobody can be seated here. It is still drawn so you can find it and put it
            back.
          </Text>
        )}

        {/* A table needs a name because staff say it out loud — "drinks on Beach 3". A palm tree is
            a shape on a drawing; naming every one "Palm 1", "Palm 2" is bookkeeping nobody reads.
            Optional there, and the plan simply draws it unlabelled. */}
        <TextInput
          label="Name"
          description={
            assignable ? 'What staff call it' : 'Optional — only if you need to tell it from another'
          }
          placeholder={assignable ? 'Beach 3' : 'Usually left blank'}
          value={String(draft.label ?? '')}
          onChange={(e) => set({ label: e.currentTarget.value })}
          data-autofocus
          required={assignable}
        />

        <Group grow align="flex-start">
          <Select
            label="What is it?"
            // Grouped so nobody hunting for "Table" scrolls past "Palm tree" to find it. Choosing
            // scenery flips `assignable`, which is what the register reads — the label the designer
            // picks and the question the register asks stay one decision.
            data={[
              { group: 'Somewhere to sit', items: enumOptions('spotKind') },
              { group: 'Part of the room', items: enumOptions('spotDecorKind') },
            ]}
            value={String(draft.kind)}
            onChange={(v) => {
              const kind = v ?? 'table';
              const shapeDefaults = DECOR_DEFAULTS[kind];
              const nowAssignable = !shapeDefaults;
              set(
                shapeDefaults
                  ? { kind, assignable: false, ...shapeDefaults, ...traitsPatch(kind, false) }
                  : {
                      kind,
                      assignable: true,
                      // A stool arrives stool-shaped and stool-sized; a lounger arrives long.
                      ...(KIND_DEFAULTS[kind] ?? {}),
                      // Last, so it overrides anything the old kind left: a four-top switched to a
                      // lounger must stop claiming it seats four and joins its neighbor.
                      ...traitsPatch(kind, nowAssignable),
                    },
              );
            }}
            comboboxProps={{ withinPortal: true }}
          />
          {assignable && traits.fixedSeats === undefined && (
            <NumberInput
              label="Seats"
              description="On its own"
              value={seats}
              onChange={(v) => set({ seats: Number(v) || 0 })}
              min={0}
              max={40}
            />
          )}
        </Group>

        {assignable && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              What does it look like?
            </Text>
            {/* Offered per kind: a stool has no business being a hexagon table, and a shape list
                that includes every option makes the plan harder to draw, not more flexible. */}
            <SegmentedControl
              fullWidth
              value={String(draft.shape)}
              onChange={(v) => set({ shape: v })}
              data={shapesFor(String(draft.kind))}
            />
          </div>
        )}

        {/* Size was offered for a palm tree but not for a table, so every table was the same
            64-unit square whatever it seated: the eight-top on the beach drew exactly like a
            two-top, and picking "Rectangular table" changed nothing on the plan. A floor plan you
            cannot make look like your own floor is a picture nobody trusts. */}
        <div>
          <Text size="sm" fw={500} mb={4}>
            Size on the plan
          </Text>
          <Group grow>
            <NumberInput
              label="Across"
              value={Number(draft.width) || 0}
              onChange={(v) => set({ width: v === '' ? '' : Number(v) })}
              min={8}
              clampBehavior="blur"
              step={8}
            />
            <NumberInput
              label="Deep"
              value={Number(draft.height) || 0}
              onChange={(v) => set({ height: v === '' ? '' : Number(v) })}
              min={8}
              clampBehavior="blur"
              step={8}
            />
          </Group>
          <Text size="xs" c="dimmed" mt={4}>
            Not measurements — just how big this draws next to everything else in the area.
          </Text>
        </div>

        {traits.mobility && (
          <div>
            <Text size="sm" fw={500} mb={4}>
              Can it be moved?
            </Text>
            <SegmentedControl
              fullWidth
              value={String(draft.mobility)}
              onChange={(v) => set({ mobility: v })}
              data={[
                { value: 'fixed', label: 'Fixed in place' },
                { value: 'movable', label: 'Can be moved' },
              ]}
            />
            <Text size="xs" c="dimmed" mt={4}>
              Beach tables are set into the ground; bar tables get pushed around.
            </Text>
          </div>
        )}

        {assignable && traits.combinable && (
          <Checkbox
            label="Can be joined to another for a bigger group"
            checked={Boolean(draft.combinable)}
            onChange={(e) => set({ combinable: e.currentTarget.checked })}
          />
        )}
        {!assignable && (
          <Text size="xs" c="dimmed">
            Part of the room — it shows on the plan, but no tab can be opened against it.
          </Text>
        )}

        <Group justify="space-between" mt="sm">
          {spot ? (
            offThePlan ? (
              <Button
                variant="subtle"
                color="teal"
                onClick={() => setInUse.mutate(true)}
                loading={setInUse.isPending}
              >
                Put back on the plan
              </Button>
            ) : (
              <Button
                variant="subtle"
                color="red"
                onClick={() =>
                  modals.openConfirmModal({
                    title: `Remove ${spot.label || 'this'}?`,
                    children: <Text size="sm">It comes off the plan for good.</Text>,
                    labels: { confirm: 'Remove it', cancel: 'Keep it' },
                    confirmProps: { color: 'red' },
                    onConfirm: () => remove.mutate(),
                  })
                }
                loading={remove.isPending || setInUse.isPending}
              >
                Remove
              </Button>
            )
          ) : (
            <span />
          )}
          <Group gap="xs">
            <Button variant="default" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={assignable && !String(draft.label ?? '').trim()}
            >
              {spot ? 'Save' : 'Add it'}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
